// Why this tile is empty.
//
// A chart that returns no rows currently says "No data for this filter", which
// is true of every empty chart and therefore tells nobody anything. On a
// per-experiment board it is worse than uninformative: the three reasons a tile
// comes back empty mean completely different things, and only one of them is a
// finding.
//
//  - **This source has no rows for this experiment at all.** Not a result. The
//    register knows this before the query runs, which is why it is the first
//    thing checked: a person reading a blank funnel under an experiment's name
//    concludes the funnel is flat, and it is simply the wrong view for that
//    experiment's kind.
//  - **The tile's own filters exclude everything.** A composition mistake, and
//    the fix is one click away in the panel, so the sentence names the filters.
//  - **The date window excludes everything.** Extremely common on a board built
//    for an experiment that ran last quarter, and invisible unless said.
//  - Anything else falls back to the honest generic. Guessing past what is
//    known would be worse than the sentence it replaced.
//
// Pure, so the ORDER of those checks is testable as a rule rather than as a
// rendering accident. The order matters: an experiment a source cannot cover is
// checked before the reader's filters, because telling somebody to widen their
// date range on a view that will never have their experiment sends them looking
// in the wrong place.
import type { ChartSpec, FilterState } from "@bi/shared/types";

export interface EmptyTileContext {
  chart: ChartSpec;
  filters: FilterState;
  /** The experiment this board is scoped to, or `null` when it is not scoped to
   *  one. */
  experimentKey: string | null;
  /** Source ids the register says carry rows for `experimentKey`, or `null`
   *  when the register is not known yet (cold cache, or a board that never
   *  asked). `null` is NOT an empty list: an unknown register must not be
   *  reported as "this source has nothing", which would be a confident claim
   *  about something nobody has looked up. */
  coveredSourceIds: readonly string[] | null;
}

/**
 * The sentence an empty tile shows instead of a blank panel.
 *
 * Always returns something: an empty result always has a reason worth stating,
 * even when the only honest one is "nothing matched".
 */
export function explainEmptyTile(context: EmptyTileContext): string {
  const { chart, filters, experimentKey, coveredSourceIds } = context;
  const sourceId = chart.source_id;

  if (
    experimentKey &&
    sourceId &&
    coveredSourceIds !== null &&
    coveredSourceIds.length > 0 &&
    !coveredSourceIds.includes(sourceId)
  ) {
    return `No rows: ${sourceId} carries nothing for ${experimentKey}. That is a fact about which views cover this kind of experiment, not a result. The sources that do carry it are ${coveredSourceIds.join(", ")}.`;
  }

  const ownFilters = chart.query.filters ?? [];
  if (ownFilters.length > 0) {
    const columns = [
      ...new Set(
        ownFilters
          .map((f) => ("subject" in f ? f.subject : null))
          .filter((c): c is string => Boolean(c)),
      ),
    ];
    if (columns.length > 0) {
      return `No rows: this tile's own filter on ${columns.join(", ")} excludes everything in the current window. Clear it in the panel on the right, or widen the window.`;
    }
  }

  if (filters.time_range_start || filters.time_range_end) {
    const from = filters.time_range_start ?? "the beginning";
    const to = filters.time_range_end ?? "now";
    return `No rows between ${from} and ${to}. An experiment that ran outside this window shows exactly like this, so widen the range before reading anything into it.`;
  }

  return "No rows matched. Nothing here is narrowed by a date range, so this is the whole source answering with nothing.";
}
